import { DBReducerMap } from "@common-reducers/DBServiceThunks";
import { safeDispatch as dispatch, getStateDirectly } from '@common/state/state-provider';
import { UpdateEventMessageData } from "./types";
import { logNewlyAddedCampaigns } from "@common/utils/campaign-log-utils";
import { Campaign } from "@common-models/campaign-model";
import { GenericState } from "@common-reducers/DBServiceReducersInterfaces";
import { CommonRootState } from "@common/types/common-root-state-type";
// Note: MQTTSubscriber is imported dynamically inside processMessage to avoid circular dependency

// WhatsApp tools handlers are in @common/tools/whatsapp/notifier-handlers.ts
// Import directly from there to avoid circular dependencies:
// import { handleWhatsappInterfaceEvents, processWhatsappInterfaceCommand } from '@common/tools/whatsapp';

// ============================================================================
// Helper Functions (DRY)
// ============================================================================

/**
 * Log newly added campaigns if path is 'campaigns' (DRY helper)
 */
async function logCampaignsIfNeeded(path: string, entity: any): Promise<void> {
  if (path !== 'campaigns' || !entity) {
    return;
  }

  try {
    const state = getStateDirectly() as CommonRootState;
    const currentState = state.DBCampaignReducer;
    const campaigns = Array.isArray(entity) ? entity : [entity];
    // Dynamic import to avoid circular dependency
    const { MQTTSubscriber } = await import(/* webpackMode: "eager" */ './MQTTSubscriber');
    const mqttSubscriber = MQTTSubscriber.getInstance();
    const clientId = mqttSubscriber.getCurrentClientId();
    const clientType = mqttSubscriber.getCurrentClientType();
    await logNewlyAddedCampaigns(
      campaigns as Campaign[],
      currentState as GenericState<Campaign>,
      dispatch,
      clientId,
      clientType
    );
  } catch (error) {
    console.error('Error logging newly added campaign:', error);
    // Don't fail the operation if logging fails
  }
}

/**
 * Slices that must not hold soft-deleted rows. Boards: a deleted board in the store is
 * an invisible board that "Create task" still finds by chat id. NOT general — other
 * slices keep deleted rows on purpose (a sent scheduled message is a soft-deleted
 * user-messages row: the planner's sent history). The recycle bin lists from its own
 * fetch, and a restored board returns through its patch with `deleted: false`.
 */
const NO_DELETED_ROWS = new Set(['boards']);
const keepsNoDeletedRows = (path: string) => NO_DELETED_ROWS.has(path);

// Create message processing handler
export const processMessage = async (message: UpdateEventMessageData) => {

  // Verbose logging removed - only log errors and significant events
  // Enable DEBUG_NOTIFIER=true in console for verbose logging
  const isDebug = typeof window !== 'undefined' && (window as any).DEBUG_NOTIFIER;

  if (isDebug) {
    console.log(`[Notifier] Processing: ${message.method} ${message.path}`);
  }

  const dbReducerSlice = DBReducerMap.get(message.path);
  if (dbReducerSlice) {
    switch (message.method) {
      case "create": {
        // Log newly added campaigns if this is a campaign create event
        await logCampaignsIfNeeded(message.path, message.entity);
        dispatch(dbReducerSlice.slice.actions.addOne(message.entity));
        break;
      }
      case "patch": {
        const state = getStateDirectly();
        if (message.entity) {
          if (Array.isArray(message.entity)) {
            // Version-guarded: a stale or out-of-order realtime event must not
            // overwrite a newer local copy (e.g. revert a just-deleted entity).
            dispatch(dbReducerSlice.slice.actions.conditionalUpdateMany(message.entity));
          } else {
            const entity = dbReducerSlice.selectors.selectById(state, message.entity?._id);
            const dropsWhenDeleted = keepsNoDeletedRows(message.path) && message.entity?.deleted === true;
            if (entity) {
              dispatch(
                dbReducerSlice.slice.actions.conditionalUpdateOne(message.entity)
              );
              // Removed only if the (version-guarded) update actually landed, so a stale
              // "deleted" event can't drop a newer, restored copy.
              if (dropsWhenDeleted && dbReducerSlice.selectors.selectById(getStateDirectly(), message.entity._id)?.deleted === true) {
                dispatch(dbReducerSlice.slice.actions.removeOne(message.entity._id));
              }
            } else if (dropsWhenDeleted) {
              // Never add a board we don't hold that arrives already deleted: that is how a
              // just-deleted chat board came back as an invisible board for "Create task".
            } else {
              // Log newly added campaigns if this is a campaign patch event that results in addOne
              await logCampaignsIfNeeded(message.path, message.entity);
              dispatch(dbReducerSlice.slice.actions.addOne(message.entity));
            }
          }
        }
        break;
      }
      case "remove": {
        dispatch(dbReducerSlice.slice.actions.removeOne(message.entity._id));
        break;
      }
    }
  } else {
    // Only warn for unexpected paths (not every message)
    if (isDebug) {
      console.warn(`[Notifier] No DB reducer for: ${message.path}`);
    }
  }
  return true; // Message processed successfully
};


